/**
 * Location helpers for the Eli & Mag PWA.
 *
 * Browsers cannot get position when the app is closed — we can only capture
 * location while the tab/PWA is open. This module handles:
 * - Support & permission probing
 * - Native prompt trigger
 * - `watchPosition` with sensible throttling while the app is foregrounded
 */

import api from "@/lib/api";

const DECISION_KEY = "em_location_decision";     // 'granted' | 'denied' | 'later'
const LAST_PROMPT_KEY = "em_location_prompted_at";
const CLIENT_KEY = "em_client_id";

export function locationSupport() {
  return typeof navigator !== "undefined" && "geolocation" in navigator;
}

export function clientId() {
  let id = localStorage.getItem(CLIENT_KEY);
  if (!id) {
    // 96-bit random id — stable per device.
    const bytes = new Uint8Array(12);
    (window.crypto || window.msCrypto).getRandomValues(bytes);
    id = Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("");
    localStorage.setItem(CLIENT_KEY, id);
  }
  return id;
}

export async function permissionState() {
  if (!locationSupport()) return "unsupported";
  try {
    if (navigator.permissions && navigator.permissions.query) {
      const s = await navigator.permissions.query({ name: "geolocation" });
      return s.state; // 'granted' | 'denied' | 'prompt'
    }
  } catch { /* ignore */ }
  // Fallback: rely on our own stored decision.
  const d = localStorage.getItem(DECISION_KEY);
  if (d === "granted") return "granted";
  if (d === "denied") return "denied";
  return "prompt";
}

export function setLocationDecision(v) {
  localStorage.setItem(DECISION_KEY, v);
  localStorage.setItem(LAST_PROMPT_KEY, String(Date.now()));
}
export function getLocationDecision() {
  return localStorage.getItem(DECISION_KEY);
}

export async function shouldPromptLocation() {
  if (!locationSupport()) return false;
  const state = await permissionState();
  if (state === "granted" || state === "denied") return false;
  const decision = getLocationDecision();
  if (decision === "granted" || decision === "denied") return false;
  if (decision === "later") {
    const last = Number(localStorage.getItem(LAST_PROMPT_KEY) || 0);
    if (Date.now() - last < 3 * 24 * 60 * 60 * 1000) return false;
  }
  return true;
}

export function isLocationStepSettled() {
  const d = getLocationDecision();
  return d === "granted" || d === "denied" || d === "later" || d === "unsupported";
}

export function getCurrentPositionOnce(opts = {}) {
  return new Promise((resolve, reject) => {
    if (!locationSupport()) return reject(new Error("unsupported"));
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      maximumAge: 30_000,
      timeout: 15_000,
      ...opts,
    });
  });
}

export function startWatching(cb) {
  if (!locationSupport()) return () => {};
  const id = navigator.geolocation.watchPosition(
    (pos) => cb(pos),
    (err) => console.warn("[location] watch error", err),
    { enableHighAccuracy: false, maximumAge: 60_000, timeout: 30_000 }
  );
  return () => navigator.geolocation.clearWatch(id);
}

export async function pingBackend({ lat, lon, accuracy, lang }) {
  const guestName = (() => {
    try { return localStorage.getItem("em_guest_name") || null; } catch { return null; }
  })();
  const payload = {
    lat,
    lon,
    accuracy_m: accuracy,
    client_id: clientId(),
    lang,
    guest_name: guestName,
  };
  const { data } = await api.post("/location/ping", payload);
  return data;
}
